import type { FC } from "react";
import {
  useFlightCfarFareView,
  type FlightCfarFareViewProps,
} from "@b2bportal/core-flights-cfar-offers";

import {
  IconNameEnum,
  useCfarStyles,
  useModuleBEM,
} from "@b2bportal/core-themes";
import { CoreFlightsCfarOffersComponents } from "@b2bportal/core-types";
import { Trans, useI18nContext } from "@hopper-b2b/i18n";
import { getFixedPricingFromFare } from "@overrides/utilities";
import { HtsAirCfarHowItWorksDialog } from "@tenant/core-flights-cfar-offers";
import { Icon, usePriceDisplay } from "@tenant/ui";
import defaultStyles from "./FlightCfarFareView.module.scss";

const COMPONENT_KEY = CoreFlightsCfarOffersComponents.FlightCfarFareView;

export const FlightCfarFareView: FC<FlightCfarFareViewProps> = ({
  fareDetails,
  sliceIndex,
  onSelect,
  cfarOffer,
}) => {
  const styles = useCfarStyles(COMPONENT_KEY, defaultStyles);
  const [, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const { t, brand } = useI18nContext();
  // fareDetails.paxPricings already includes the CFAR premium
  // (added by createFareWithCfarPrice in the core library),
  // so no additional premium addition is needed here.
  const prices = fareDetails.paxPricings?.[0]
    ? getFixedPricingFromFare(fareDetails.paxPricings[0])
    : null;

  const { brandName, refundabilityLabel, cfarOnlyRestrictions } =
    useFlightCfarFareView({
      fareDetails,
      sliceIndex,
      cfarOffer,
    });

  const { rewardsThenPriceText } = usePriceDisplay({
    prices,
    inline: "priceOnly",
  });

  const termsAndConditionsLink = brand.urls.airCfarTerms;

  return (
    <>
      <div className={cn("header")}>
        <div className={cn("header-title")}>{brandName}</div>
        <div className={cn("header-subtitle")}>
          {/* refundabilityLabel is sanitized in useFlightCfarFareView hook; fallback is a static translation string */}
          <span
            className={cn("header-subtitle-refundability-label")}
            dangerouslySetInnerHTML={{
              __html:
                refundabilityLabel ||
                t("core-flights-cfar.partiallyRefundableBy"),
            }}
          />
          <Icon
            className={cn("header-subtitle-hts-logo")}
            useCurrentColor={false}
            iconName={IconNameEnum.htsPoweredByLogo}
            aria-hidden="true"
          />
          <HtsAirCfarHowItWorksDialog
            merchandisingContents={cfarOffer.merchandisingContents ?? {}}
            onUpgradeAndContinue={onSelect}
          />
        </div>
      </div>
      <div className={cn("details")}>
        <p className={cn("details-body-text")}>
          <Trans
            i18nKey="core-flights-cfar.sameBenefitsAs"
            components={{
              linkText: termsAndConditionsLink ? (
                // eslint-disable-next-line jsx-a11y/anchor-has-content
                <a
                  className={cn("terms-link")}
                  href={termsAndConditionsLink}
                  target="_blank"
                  rel="noopener noreferrer"
                />
              ) : (
                <span />
              ),
            }}
            values={{ brandName }}
          />
        </p>
        <hr className={cn("details-divider")} />
        <ul className={cn("details-cfar-list")}>
          {cfarOnlyRestrictions.map((restriction) => (
            <li
              className={cn("details-cfar-item")}
              key={restriction.description}
            >
              <Icon
                className={cn("details-cfar-icon")}
                iconName={IconNameEnum.fareDetailsAvailable}
              />
              {/* Content is pre-sanitized in useFlightCfarFareView hook */}
              <div
                dangerouslySetInnerHTML={{
                  __html: restriction.description,
                }}
              />
            </li>
          ))}
        </ul>
      </div>
      <div className={cn("fare-select")}>
        {prices !== null && (
          <>
            <p className={cn("continue-price")}>{rewardsThenPriceText}</p>
            <button className={cn("fare-button")} onClick={onSelect}>
              {t("continue")}
            </button>
          </>
        )}
      </div>
    </>
  );
};
